// Chat/MessagesContainer.tsx

import React, {useContext} from 'react';
import {CircularProgress} from '@mui/material';
import {FC, FCCC, FCE} from 'WideLayout/Layouts';
import RoomMessage from 'Chat/RoomMessage';
import {IMessage, IRoom} from 'Types/chat/models';
import {useTheme} from 'Theme/ThemeContext';
import {AuthContext, AuthContextType} from 'Auth/AuthContext';

interface MessagesContainerProps {
    messages: IMessage[];
    room: IRoom | null;
    renderDateLabel: (message: IMessage, previousMessage?: IMessage) => React.ReactNode;
    isLoadingMore: boolean; // Флаг загрузки (включая первичную)
    messagesEndRef: React.RefObject<HTMLDivElement>;
    messagesContainerRef: React.RefObject<HTMLDivElement>;
}

const MessagesContainer: React.FC<MessagesContainerProps> = (props) => {
    const {
        messages,
        room,
        renderDateLabel,
        isLoadingMore,
        messagesEndRef,
        messagesContainerRef
    } = props;

    const {user} = useContext(AuthContext) as AuthContextType;
    const {plt} = useTheme();

    /**
     * Проверяем, не является ли диалог «сам с собой».
     * Здесь выводим «Здесь ваше личное пространство».
     */
    const isSelfDialog = (room: IRoom | null): boolean => {
        if (!room || !user) return false;
        if (room.participants.length !== 2) return false;
        // Проверка, что оба участника — текущий пользователь
        return room.participants.every(participant => participant.id === user.id);
    };

    return (
        <FC
            grow
            scroll={'y-auto'}
            px={2}
            ref={messagesContainerRef}
            cls={'no-scrollbar'}
            style={{overflowY: 'auto', display: 'flex', flexDirection: 'column'}}
        >
            {room ? (
                <FCE
                    grow
                    g={1}
                    py={1}
                    cls={`messages`}
                    boxShadow={plt.shadows ? plt.shadows.SO005 : ''}
                    style={{display: 'flex', flexDirection: 'column'}}
                >
                    {/**
                     * 1. Если всё ещё "идёт загрузка" (isLoadingMore === true)
                     *    и НЕТ уже загруженных сообщений (messages.length === 0),
                     *    показываем большой спиннер в центре.
                     */}
                    {isLoadingMore && messages.length === 0 ? (
                        <FCCC p={2} mx={'auto'}>
                            <CircularProgress size={'100px'}/>
                        </FCCC>
                    ) : messages.length > 0 ? (
                        /**
                         * 2. Если сообщения есть, рендерим их по одному.
                         */
                        messages.map((message, index) => (
                            <React.Fragment key={message.id || index}>
                                {renderDateLabel(message, messages[index - 1])}
                                <RoomMessage
                                    message={message}
                                    room_capacity={room.max_participants}
                                />
                            </React.Fragment>
                        ))
                    ) : (
                        /**
                         * 3. Если загрузка завершена, но сообщений нет — выводим
                         *    «Здесь ваше личное пространство...» или «Начните писать...».
                         */
                        <FCCC p={2} textAlign={'center'} mx={'auto'} rounded={4}
                              fontWeight={'bold'} fontSize={22}
                              bg={plt.colors.secondary.lighter}
                              color={plt.text.primary30}
                        >
                            {isSelfDialog(room)
                                ? "Здесь ваше личное пространство. Его никто не видит. Мы рекомендуем вести здесь свой дневник и делать заметки по ходу терапии."
                                : "Начните писать первым."
                            }
                        </FCCC>
                    )}

                    {/* Прокрутка к последнему сообщению */}
                    <div ref={messagesEndRef}></div>
                </FCE>
            ) : (
                /**
                 * Если room == null (комната ещё не получена)
                 * — можно показывать общий спиннер.
                 */
                <FCCC p={2} grow justifyContent="center">
                    <CircularProgress/>
                </FCCC>
            )}
        </FC>
    );
};

export default React.memo(MessagesContainer);
